import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSmartAppBanner } from '../hooks/useSmartAppBanner';

export default function TermsPage() {
  useSmartAppBanner();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900">
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap');
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .lowercase {
          text-transform: lowercase;
        }
        .safe-padding {
          padding-left: max(1rem, env(safe-area-inset-left));
          padding-right: max(1rem, env(safe-area-inset-right));
        }
      `}</style>

      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 safe-padding">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <CirclesIcon size={28} color="#cef7fc" isActive={true} />
              <span className="text-xl sm:text-2xl font-black lowercase text-neutral-900">circles</span>
            </Link>
            <Link
              to="/"
              className="bg-[#cef7fc] hover:bg-[#b8f0f7] text-neutral-900 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all active:scale-[0.98] shadow-sm hover:shadow-md"
            >
              Get the app
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 safe-padding">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black lowercase mb-8 text-neutral-900">terms of service</h1>

        <p className="text-neutral-500 text-sm mb-8">Last updated: January 2025</p>

        <div className="prose prose-neutral max-w-none space-y-8">
          <Section title="1. acceptance of terms">
            <p>
              By downloading, installing, or using Circles ("the App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
            </p>
            <p>
              We may modify these Terms at any time. Continued use of the App after any changes constitutes your acceptance of the new Terms.
            </p>
          </Section>

          <Section title="2. eligibility">
            <p>
              You must be at least 18 years old to use Circles. By using the App, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms.
            </p>
          </Section>

          <Section title="3. account registration">
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>You must provide accurate, current, and complete information during registration</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
              <li>You may not share your account or let others access your account</li>
            </ul>
          </Section>

          <Section title="4. circles and matching">
            <h4 className="font-semibold text-neutral-800 mt-4">Creating and Joining Circles</h4>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>You may create circles with friends (2-6 members per circle)</li>
              <li>Circle admins are responsible for managing their circle's membership</li>
              <li>You can only be an active member of one circle at a time</li>
            </ul>

            <h4 className="font-semibold text-neutral-800 mt-4">Voting and Matching</h4>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>Votes are anonymous within your circle until a threshold is met</li>
              <li>A match occurs when a sufficient number of members from both circles vote positively</li>
              <li>Once matched, a group chat is created for both circles to communicate</li>
            </ul>
          </Section>

          <Section title="5. user conduct">
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>Use the App for any illegal purpose or in violation of any laws</li>
              <li>Harass, abuse, stalk, threaten, or intimidate other users</li>
              <li>Post or share content that is obscene, defamatory, hateful, discriminatory, or violent</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Upload content that infringes on intellectual property rights</li>
              <li>Spam, solicit, or advertise without authorization</li>
              <li>Attempt to access other users' accounts or private information</li>
              <li>Use bots, scripts, or automated methods to interact with the App</li>
              <li>Interfere with or disrupt the App's functionality or servers</li>
              <li>Create multiple accounts or circumvent bans</li>
              <li>Collect or harvest user data without consent</li>
            </ul>
          </Section>

          <Section title="6. content">
            <h4 className="font-semibold text-neutral-800 mt-4">Your Content</h4>
            <p>
              You retain ownership of content you create (photos, messages, etc.). By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content in connection with the App.
            </p>

            <h4 className="font-semibold text-neutral-800 mt-4">Content Guidelines</h4>
            <p>All content must comply with our community guidelines. We reserve the right to remove content that violates these Terms or is otherwise objectionable.</p>

            <h4 className="font-semibold text-neutral-800 mt-4">Content Moderation</h4>
            <p>
              We do not actively monitor all content but may review reported content. We are not responsible for content posted by users.
            </p>
          </Section>

          <Section title="7. safety">
            <p>
              Circles is designed to help groups of friends meet other groups. However, we cannot guarantee the identity or behavior of any user. You are responsible for your own safety when meeting people through the App.
            </p>
            <p className="mt-4">
              <strong>Safety Tips:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>Meet in public places for first meetings</li>
              <li>Tell friends or family about your plans</li>
              <li>Trust your instincts - if something feels wrong, leave</li>
              <li>Report suspicious or concerning behavior</li>
            </ul>
          </Section>

          <Section title="8. reporting and blocking">
            <p>
              If you encounter inappropriate behavior or content, please report it through the App. You can also block users or leave chats at any time.
            </p>
            <p>
              We take reports seriously and may take action including warnings, suspensions, or permanent bans based on our investigation.
            </p>
          </Section>

          <Section title="9. termination">
            <p>
              We may suspend or terminate your account at any time for any reason, including violation of these Terms. You may delete your account at any time through the App settings.
            </p>
            <p>
              Upon termination, your right to use the App ceases immediately. Provisions that by their nature should survive termination will survive.
            </p>
          </Section>

          <Section title="10. disclaimers">
            <p>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>
            <p className="mt-4">
              WE ARE NOT RESPONSIBLE FOR THE CONDUCT OF ANY USER, WHETHER ONLINE OR OFFLINE. WE DO NOT CONDUCT BACKGROUND CHECKS ON USERS.
            </p>
          </Section>

          <Section title="11. limitation of liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="mt-4">
              OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR USE OF THE APP SHALL NOT EXCEED THE AMOUNT YOU PAID US, IF ANY, IN THE PAST 12 MONTHS.
            </p>
          </Section>

          <Section title="12. indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless Circles and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the App, your violation of these Terms, or your violation of any rights of another.
            </p>
          </Section>

          <Section title="13. governing law">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </Section>

          <Section title="14. dispute resolution">
            <p>
              Any disputes arising from these Terms or your use of the App shall first be attempted to be resolved through informal negotiation. If we cannot resolve the dispute informally, you agree to submit to binding arbitration.
            </p>
            <p className="mt-4">
              <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
          </Section>

          <Section title="15. general provisions">
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Circles regarding the App.</li>
              <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions will continue in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision does not constitute a waiver of that right or provision.</li>
              <li><strong>Assignment:</strong> You may not assign your rights under these Terms. We may assign our rights without restriction.</li>
            </ul>
          </Section>

          <Section title="16. contact us">
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> <a href="mailto:hello@getcircles.com" className="text-[#0ea5e9] hover:underline">hello@getcircles.com</a>
            </p>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 sm:py-10 px-4 sm:px-6 safe-padding bg-white">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4 sm:gap-6">
          <Link to="/" className="text-xl font-black lowercase text-neutral-900">circles</Link>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-neutral-500">
            <Link to="/privacy" className="hover:text-gray-900 transition lowercase">privacy</Link>
            <Link to="/terms" className="hover:text-gray-900 transition lowercase">terms</Link>
            <a href="mailto:hello@getcircles.com" className="hover:text-gray-900 transition lowercase">support</a>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm">
            &copy; {new Date().getFullYear()} circles
          </p>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xl sm:text-2xl font-bold lowercase text-neutral-900 mb-4">{title}</h3>
      <div className="text-neutral-600 space-y-3 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function CirclesIcon({ size = 28, color = '#cef7fc', isActive = false }) {
  const outerStrokeWidth = 1.2;
  const innerStrokeWidth = 0.9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'block', filter: 'drop-shadow(1px 1px 0px rgba(0, 0, 0, 0.9))' }}
    >
      {isActive ? (
        <>
          <path
            d="M14.92,16.72c2.69.92,6.1-1.5,6.12-4.41.19-2.8-2.16-5.14-4.97-5.16-.47-.01-.85.07-1,.28-.24.31.05,1,.23,1.53.79,2.38.47,5-.47,7.27-.02.13-.03.29.03.42l.06.07Z"
            fill={color}
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
          <path
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={innerStrokeWidth}
          />
          <circle
            cx="7.22"
            cy="11.89"
            r="6.03"
            fill={color}
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
        </>
      ) : (
        <>
          <path
            d="M14.68,16.7c.48.14.99.21,1.51.2,2.69-.07,4.82-2.32,4.74-5.01s-2.32-4.82-5.01-4.74c-.4.01-.79.07-1.16.17"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
          <path
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={innerStrokeWidth}
          />
          <circle
            cx="7.22"
            cy="11.89"
            r="6.03"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
        </>
      )}
    </svg>
  );
}
